import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ShoppingCart,
  ArrowLeft,
  MagnifyingGlass,
  Scissors,
  Funnel,
  Warning,
  WarningCircle,
  MinusCircle,
  CheckCircle,
  FileXls,
  FilePdf
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { Toaster, toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIAS = [
  { value: 'guia-1', label: 'Guia 1 - Prod. Técnicos' },
  { value: 'guia-1b', label: 'Guia 1B - Tintas' },
  { value: 'guia-2', label: 'Guia 2 - Revenda' },
  { value: 'guia-3', label: 'Guia 3 - Uso Interno' },
  { value: 'guia-4', label: 'Guia 4 - Limpeza' },
  { value: 'guia-5', label: 'Guia 5 - Copa' },
  { value: 'guia-6', label: 'Guia 6 - Escritório' },
  { value: 'guia-7', label: 'Guia 7 - Descartáveis' },
  { value: 'guia-8', label: 'Guia 8 - Reforma' }
];

const getCategoriaLabel = (value) => {
  const cat = CATEGORIAS.find(c => c.value === value);
  return cat ? cat.label : value || '-';
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
};

// Classifica urgência do item de compra
const getUrgencia = (item) => {
  if (item.saldo_atual <= 0 && item.estoque_minimo > 0) {
    return { level: 'zerado', label: 'Zerado', color: '#DC2626', bg: '#DC262610', icon: Warning };
  }
  if (item.saldo_atual < item.estoque_minimo && item.estoque_minimo > 0) {
    return { level: 'baixo', label: 'Abaixo do mínimo', color: '#F59E0B', bg: '#F59E0B10', icon: WarningCircle };
  }
  return { level: 'normal', label: 'Normal', color: '#71717A', bg: '#71717A10', icon: MinusCircle };
};

export const Compras = () => {
  const navigate = useNavigate();
  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFornecedor, setFilterFornecedor] = useState('all');
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [exporting, setExporting] = useState(null);

  const fetchCompras = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/pacman/compras`);
      setCompras(response.data);
    } catch (error) {
      toast.error('Erro ao carregar sugestões de compra');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompras();
  }, [fetchCompras]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const params = new URLSearchParams();
      if (filterCategoria !== 'all') params.set('categoria', filterCategoria);
      if (filterFornecedor !== 'all') params.set('fornecedor', filterFornecedor);
      if (searchTerm) params.set('search', searchTerm);
      
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`${API}/pacman/compras/export/${format}${qs}`, {
        responseType: 'blob'
      });
      
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const mime = format === 'excel' 
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        : 'application/pdf';
      
      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `compras_salonup.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      const hasFilters = filterCategoria !== 'all' || filterFornecedor !== 'all' || searchTerm;
      toast.success(`Lista de compras ${hasFilters ? 'filtrada' : 'completa'} exportada em ${ext.toUpperCase()}!`);
    } catch (error) {
      toast.error('Erro ao exportar lista de compras');
    } finally {
      setExporting(null);
    }
  };

  const fornecedores = useMemo(() => 
    [...new Set(compras.map(c => c.fornecedor).filter(Boolean))].sort(),
    [compras]
  );

  const filteredCompras = useMemo(() => compras.filter(item => {
    const matchesSearch = 
      item.produto?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.fornecedor?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFornecedor = filterFornecedor === 'all' || item.fornecedor === filterFornecedor;
    const matchesCategoria = filterCategoria === 'all' || item.categoria === filterCategoria;
    return matchesSearch && matchesFornecedor && matchesCategoria;
  }), [compras, searchTerm, filterFornecedor, filterCategoria]);

  // Stats
  const stats = useMemo(() => {
    const zerados = filteredCompras.filter(i => i.saldo_atual <= 0).length;
    const abaixo = filteredCompras.filter(i => i.saldo_atual > 0).length;
    return {
      itens: filteredCompras.length,
      zerados,
      abaixo,
      quantidade: filteredCompras.reduce((acc, i) => acc + (i.sugestao_compra || 0), 0),
      custoTotal: filteredCompras.reduce((acc, i) => acc + (i.custo_estimado || 0), 0)
    };
  }, [filteredCompras]);

  return (
    <div className="min-h-screen bg-[#F7F6F4]" data-testid="compras-page">
      <Toaster position="top-right" richColors />
      <div className="salonup-container">
        {/* Header */}
        <header className="salonup-header animate-fade-in">
          <div className="salonup-brand">
            <div className="salonup-logo-mark">
              <Scissors size={22} weight="bold" />
            </div>
            <span className="salonup-logo-text">Salon<span>Up</span></span>
          </div>
          
          <button 
            onClick={() => navigate('/gestao/pacman')}
            data-testid="btn-back"
            className="salonup-btn-back"
          >
            <ArrowLeft size={18} weight="bold" />
            <span>Voltar para PAC MAN</span>
          </button>
        </header>

        {/* Breadcrumb */}
        <div className="mb-4 animate-fade-in opacity-0 delay-100" style={{ animationFillMode: 'forwards' }}>
          <p className="salonup-text-xs">
            <span className="cursor-pointer hover:text-[#1B4332] transition-colors" onClick={() => navigate('/')}>Home</span>
            <span className="mx-2">·</span>
            <span className="cursor-pointer hover:text-[#1B4332] transition-colors" onClick={() => navigate('/gestao')}>Gestão</span>
            <span className="mx-2">·</span>
            <span className="cursor-pointer hover:text-[#1B4332] transition-colors" onClick={() => navigate('/gestao/pacman')}>PAC MAN</span>
            <span className="mx-2">·</span>
            <span className="text-[#18181B] font-medium">Compras</span>
          </p>
        </div>

        {/* Title Section */}
        <section className="mb-8 animate-fade-in-up opacity-0 delay-150" style={{ animationFillMode: 'forwards' }}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#52B78812' }}>
                <ShoppingCart size={28} weight="regular" style={{ color: '#52B788' }} />
              </div>
              <div>
                <h1 className="salonup-heading-lg">Compras</h1>
                <p className="salonup-text-sm mt-1">Sugestão de reposição baseada no estoque mínimo de cada produto</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                onClick={() => handleExport('excel')}
                disabled={exporting !== null || compras.length === 0}
                variant="outline"
                className="border-[#1B4332] text-[#1B4332] hover:bg-[#1B4332]/5 rounded-xl px-4 py-2.5 h-auto"
                data-testid="btn-export-excel"
              >
                <FileXls size={20} weight="bold" className="mr-2" />
                {exporting === 'excel' ? 'Exportando...' : 'Excel'}
              </Button>
              <Button 
                onClick={() => handleExport('pdf')}
                disabled={exporting !== null || compras.length === 0}
                variant="outline"
                className="border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626]/5 rounded-xl px-4 py-2.5 h-auto"
                data-testid="btn-export-pdf"
              >
                <FilePdf size={20} weight="bold" className="mr-2" />
                {exporting === 'pdf' ? 'Exportando...' : 'PDF'}
              </Button>
            </div>
          </div>
        </section>

        {/* Summary Cards */}
        <section className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4 animate-fade-in-up opacity-0 delay-200" style={{ animationFillMode: 'forwards' }}>
          <div className="salonup-card p-5 border-l-4 border-l-[#52B788]">
            <p className="salonup-text-xs mb-1">Itens para Repor</p>
            <p className="text-3xl font-semibold text-[#18181B]" data-testid="stat-itens">{stats.itens}</p>
          </div>
          <div className="salonup-card p-5 border-l-4 border-l-[#DC2626]">
            <p className="salonup-text-xs mb-1">Zerados</p>
            <p className="text-3xl font-semibold text-[#DC2626]" data-testid="stat-zerados">{stats.zerados}</p>
          </div>
          <div className="salonup-card p-5 border-l-4 border-l-[#F59E0B]">
            <p className="salonup-text-xs mb-1">Abaixo do Mínimo</p>
            <p className="text-3xl font-semibold text-[#F59E0B]" data-testid="stat-abaixo">{stats.abaixo}</p>
          </div>
          <div className="salonup-card p-5 bg-gradient-to-r from-[#1B4332]/5 to-transparent">
            <p className="salonup-text-xs mb-1">Custo Estimado</p>
            <p className="text-2xl font-semibold text-[#1B4332]" data-testid="stat-custo">{formatCurrency(stats.custoTotal)}</p>
          </div>
        </section>

        {/* Filters */}
        <section className="mb-6 animate-fade-in-up opacity-0 delay-250" style={{ animationFillMode: 'forwards' }}>
          <div className="salonup-card p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <Input 
                  placeholder="Buscar por nome, SKU ou fornecedor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-11 border-[#E4E4E7] rounded-xl"
                  data-testid="search-input"
                />
              </div>
              <div className="w-full md:w-56">
                <Select value={filterCategoria} onValueChange={setFilterCategoria}>
                  <SelectTrigger className="h-11 border-[#E4E4E7] rounded-xl" data-testid="filter-categoria">
                    <Funnel size={18} className="mr-2 text-[#A1A1AA]" />
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as categorias</SelectItem>
                    {CATEGORIAS.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {fornecedores.length > 0 && (
                <div className="w-full md:w-56">
                  <Select value={filterFornecedor} onValueChange={setFilterFornecedor}>
                    <SelectTrigger className="h-11 border-[#E4E4E7] rounded-xl" data-testid="filter-fornecedor">
                      <SelectValue placeholder="Fornecedor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os fornecedores</SelectItem>
                      {fornecedores.map(f => (
                        <SelectItem key={f} value={f}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Compras Table */}
        <section className="animate-fade-in-up opacity-0 delay-300" style={{ animationFillMode: 'forwards' }}>
          <div className="salonup-card overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <p className="salonup-text-body">Carregando sugestões de compra...</p>
              </div>
            ) : filteredCompras.length === 0 ? (
              <div className="p-12 text-center">
                <CheckCircle size={48} weight="light" className="mx-auto mb-4 text-[#52B788]" />
                <p className="salonup-heading-md mb-2">
                  {searchTerm || filterFornecedor !== 'all' || filterCategoria !== 'all'
                    ? 'Nenhum item encontrado com esses filtros'
                    : 'Estoque em dia!'}
                </p>
                <p className="salonup-text-sm">
                  {searchTerm || filterFornecedor !== 'all' || filterCategoria !== 'all'
                    ? 'Tente ajustar os filtros de busca'
                    : 'Todos os produtos com estoque mínimo definido estão com saldo adequado.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="compras-table">
                  <thead className="bg-[#F7F6F4] border-b border-[#E4E4E7]">
                    <tr>
                      <th className="text-left p-4 salonup-label">Urgência</th>
                      <th className="text-left p-4 salonup-label">Produto</th>
                      <th className="text-left p-4 salonup-label hidden lg:table-cell">Categoria</th>
                      <th className="text-left p-4 salonup-label hidden md:table-cell">Fornecedor</th>
                      <th className="text-right p-4 salonup-label">Saldo</th>
                      <th className="text-right p-4 salonup-label">Mínimo</th>
                      <th className="text-right p-4 salonup-label">Sugestão</th>
                      <th className="text-right p-4 salonup-label hidden md:table-cell">Últ. Custo</th>
                      <th className="text-right p-4 salonup-label">Custo Est.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCompras.map((item, index) => {
                      const urgencia = getUrgencia(item);
                      const UrgIcon = urgencia.icon;
                      
                      return (
                        <tr 
                          key={item.id} 
                          className={`border-b border-[#E4E4E7] transition-colors ${
                            urgencia.level === 'zerado' 
                              ? 'bg-red-50/60 hover:bg-red-50' 
                              : urgencia.level === 'baixo'
                              ? 'bg-amber-50/40 hover:bg-amber-50/60'
                              : 'hover:bg-[#F7F6F4]/50'
                          }`}
                          data-testid={`compra-row-${index}`}
                        >
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-8 h-8 rounded-lg flex items-center justify-center"
                                style={{ backgroundColor: urgencia.bg }}
                              >
                                <UrgIcon size={18} weight="fill" style={{ color: urgencia.color }} />
                              </div>
                              <span className="text-xs font-medium hidden sm:block" style={{ color: urgencia.color }}>
                                {urgencia.label}
                              </span>
                            </div>
                          </td>
                          <td className="p-4">
                            <div>
                              <p className="font-medium text-[#18181B]">{item.produto}</p>
                              <p className="text-xs text-[#71717A] font-mono">{item.sku}</p>
                            </div>
                          </td>
                          <td className="p-4 hidden lg:table-cell">
                            <span className="text-sm text-[#71717A]">{getCategoriaLabel(item.categoria)}</span>
                          </td>
                          <td className="p-4 hidden md:table-cell">
                            <span className="text-sm text-[#71717A]">{item.fornecedor || '-'}</span>
                          </td>
                          <td className="p-4 text-right">
                            <span className={`font-semibold ${
                              urgencia.level === 'zerado' ? 'text-red-600' : 
                              urgencia.level === 'baixo' ? 'text-amber-600' : 
                              'text-[#18181B]'
                            }`}>
                              {item.saldo_atual}
                            </span>
                          </td>
                          <td className="p-4 text-right">
                            <span className="text-[#71717A]">{item.estoque_minimo}</span>
                          </td>
                          <td className="p-4 text-right">
                            <span className="font-semibold text-[#52B788] bg-[#52B788]/10 px-2.5 py-1 rounded-lg">
                              +{item.sugestao_compra}
                            </span>
                          </td>
                          <td className="p-4 text-right hidden md:table-cell">
                            <span className="text-[#71717A]">{formatCurrency(item.ultimo_custo)}</span>
                          </td>
                          <td className="p-4 text-right">
                            <span className="font-medium text-[#18181B]">{formatCurrency(item.custo_estimado)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-[#F7F6F4] border-t-2 border-[#E4E4E7]">
                    <tr>
                      <td colSpan={6} className="p-4 text-right font-medium text-[#71717A]">Total:</td>
                      <td className="p-4 text-right">
                        <span className="font-semibold text-[#52B788]">{stats.quantidade}</span>
                      </td>
                      <td className="p-4 hidden md:table-cell"></td>
                      <td className="p-4 text-right">
                        <span className="font-semibold text-[#1B4332]">{formatCurrency(stats.custoTotal)}</span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
          
          {filteredCompras.length > 0 && (
            <p className="salonup-text-xs mt-4 text-center">
              {filteredCompras.length} ite{filteredCompras.length !== 1 ? 'ns' : 'm'} para reposição
            </p>
          )}
        </section>

        {/* Footer */}
        <footer className="salonup-footer animate-fade-in opacity-0" style={{ animationDelay: '500ms', animationFillMode: 'forwards' }}>
          <p className="salonup-text-xs">SalonUp — O sistema operacional do salão moderno</p>
        </footer>
      </div>
    </div>
  );
};

export default Compras;
